import { uiText, useUiLanguage } from '@/i18n/interface';

import React from 'react';
import { Button } from "@/components/ui/button";
import { Trash2, ChevronDown, ChevronUp, Check, X } from 'lucide-react';

interface TrainingSession {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  max_participants: number | null;
  instructor_id: string | null;
  created_at: string;
  attendance_count?: number;
}

interface SessionActionsProps {
  session: (TrainingSession | any) & { type: 'training' | 'promotion' };
  userRole: string | undefined;
  userResponse: boolean | null;
  showAttendance: boolean;
  onEdit?: (session: TrainingSession) => void;
  onDelete?: (sessionId: string) => void;
  onNotify?: (session: TrainingSession) => void;
  onToggleAttendance: () => void;
  onAttendanceResponse: (willAttend: boolean) => void;
  onConfirmPromotionAttendance?: (sessionId: string, willAttend: boolean) => void;
}

const SessionActions: React.FC<SessionActionsProps> = ({
  session,
  userRole,
  userResponse,
  showAttendance,
  onEdit,
  onDelete,
  onNotify,
  onToggleAttendance,
  onAttendanceResponse,
  onConfirmPromotionAttendance
}) => {
  useUiLanguage();
  if ((userRole === 'admin' || userRole === 'super_user') && session.type === 'training') {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEdit && onEdit(session as TrainingSession)}
          className="min-w-fit"
        >
          {uiText("Edit")}</Button>
        <Button
          size="sm"
          className="bg-red-900 hover:bg-red-800 min-w-fit"
          onClick={() => onNotify && onNotify(session as TrainingSession)}
        >
          {uiText("Notify")}</Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onToggleAttendance}
          className="min-w-fit"
        >
          {showAttendance ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          {uiText("Attendance")}</Button>
        <Button
          size="sm"
          variant="destructive"
          onClick={() => onDelete && onDelete(session.id)}
          className="min-w-fit"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  if (userRole === 'student' && session.type === 'training') {
    return (
      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onAttendanceResponse(false)}
          className={`flex-1 sm:flex-initial min-w-0 ${userResponse === false ? "bg-red-100 border-red-300" : ""}`}
        >
          <X className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">{uiText("Cannot Attend")}</span>
        </Button>
        <Button
          size="sm"
          onClick={() => onAttendanceResponse(true)}
          className={`flex-1 sm:flex-initial min-w-0 bg-red-900 hover:bg-red-800 ${userResponse === true ? "bg-red-700" : ""}`}
        >
          <Check className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">{uiText("Will Attend")}</span>
        </Button>
      </div>
    );
  }

  if (userRole === 'student' && session.type === 'promotion') {
    return (
      <div className="w-full sm:w-auto">
        <Button
          size="sm"
          className="w-full sm:w-auto bg-red-900 hover:bg-red-800"
          onClick={() => onConfirmPromotionAttendance && onConfirmPromotionAttendance(session.id, true)}
        >
          {uiText("Confirm Attendance")}</Button>
      </div>
    );
  }

  return null;
};

export default SessionActions;
